import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import { listBdcAdjustments } from "../../../services";
import { BdcInvoiceSummary, BdcQtyAdjustment, BdcShipmentHeader } from "../../../types/bdc.type";
import { pcs, thaiDateTime } from "../format";
import { CARD_SX } from "../styles";
import Tag from "../Tag";
import { buildTimeline } from "./history";

const PAGE_SIZES = [20, 50, 100];

/** ประวัติ — milestones (นำเข้า, ยืนยัน DO, INV, ยกเลิก) and every ยอดส่ง change, newest first. */
export default function HistoryTab({
  header,
  invoices,
  version,
}: Readonly<{
  header: BdcShipmentHeader;
  invoices: BdcInvoiceSummary[];
  /** Bumped by the page after a save so the list reloads. */
  version: number;
}>) {
  const [rows, setRows] = useState<BdcQtyAdjustment[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcAdjustments(header.po_number, page + 1, limit);
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [header.po_number, page, limit]);

  useEffect(() => {
    load();
  }, [load, version]);

  const events = buildTimeline(header, invoices);

  return (
    <Stack gap={2}>
      <Card sx={{ ...CARD_SX, p: 2 }}>
        <Typography fontWeight={600} mb={1}>
          เหตุการณ์
        </Typography>
        <table className="bdc-t">
          <tbody>
            {events.map((event, i) => (
              <tr key={`${event.at}-${i}`}>
                <td style={{ width: 160 }}>{thaiDateTime(event.at)}</td>
                <td>
                  <Tag tone={event.tone}>{event.label}</Tag>
                  {event.detail && <span className="sub"> · เหตุผล: {event.detail}</span>}
                </td>
                <td className="sub">{event.by ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card sx={CARD_SX}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E3E5EA", display: "flex", gap: 1.5, alignItems: "center" }}>
          <Typography fontWeight={600}>ประวัติการปรับยอดส่ง</Typography>
          <Tag tone="mut">{pcs(count)} รายการ</Tag>
        </Box>
        {loading && <LinearProgress />}
        {rows.length === 0 && !loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography className="sub">ยังไม่มีการปรับยอดส่ง — ยอดส่ง = ยอด PO</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>ร้าน</th>
                  <th>สินค้า</th>
                  <th className="n">ก่อน</th>
                  <th className="n">หลัง</th>
                  <th>วิธี</th>
                  <th>โดย</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.save_uuid}-${row.store_code}-${row.barcode}`}>
                    <td>{thaiDateTime(row.created_at)}</td>
                    <td>
                      <span className="mono">{row.store_code}</span> {row.store_name ?? ""}
                    </td>
                    <td>{row.item_name ?? <span className="mono">{row.barcode}</span>}</td>
                    <td className="n">{pcs(row.before_qty)}</td>
                    <td className="n">
                      <b>{pcs(row.after_qty)}</b>
                    </td>
                    <td>{row.mode === "auto" ? <Tag tone="info">เกลี่ย Auto</Tag> : <Tag tone="mut">แก้รายร้าน</Tag>}</td>
                    <td className="sub">{row.created_by_name ?? row.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(_e, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="ต่อหน้า"
        />
      </Card>
    </Stack>
  );
}
